// QR Kiosk v2 — KV2-7 dashboard my-tasks entry (PG-3 un-hide). Kiosk-legibility
// rework 2026-07-23 (KV2_DESIGN_REVIEW_FEEDBACK.md §1): reshaped from a 60px row
// into a uniform DashTile rendered INSIDE the DashboardTileGrid board. Hide-first
// semantics unchanged — the dashboard NEVER shows task errors: hidden for Viewer,
// for a non-scoped/failed load, and when the open count is 0 (a Maintenance outage
// silently hides it; the list page renders task errors). Warms the §8 list cache.
// Tokens only; no MUI/dt/hex.
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import useTranslation from "../../../shared/hooks/useTranslation";
import { Roles } from "../../../account/models/AccountModels";
import { Machine } from "../../models/machinesModels";
import { GD } from "../shared/glyphs";
import { useSurfaceState } from "../shared/useSurfaceState";
import { isGuestRole } from "../guest/guestSession";
import DashTile from "../dashboard/DashTile";
import { getKioskTasks } from "./kioskTasksSlice";
import { openTaskCount, tasksRowLabelKey } from "./taskUtils";

export interface MyTasksRowProps {
  machine: Machine; // the ROUTE-SCOPED machine (never the raw shared field)
}

export function MyTasksRow({ machine }: MyTasksRowProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("public");
  const role = useAppSelector((s) => s.account.user?.role);
  const { tasks, tasksMachineId } = useAppSelector((s) => s.kioskTasks);
  const stTasks = useSurfaceState("tasks", machine.id);

  // The kiosk task surface is COMPANY-ONLY: Maintenance's KioskWorkOrdersController gates on
  // ALL_COMPANY_ROLES, which DELIBERATELY excludes the Guest role (guests have no assigned work
  // orders). A guest fetch therefore 403s — and because the global axios 403 handler
  // (core/errorHandler) dispatches showAccessDenied for any non-/public/ path, that stray 403
  // would nuke the WHOLE guest kiosk to "Prístup zamietnutý". So skip the fetch for guests, the
  // same way it is skipped for Viewer (both = "no tasks", matched WITHOUT firing a doomed call).
  const noTasksForRole = role === Roles.VIEWER || isGuestRole(role);

  useEffect(() => {
    if (noTasksForRole) return; // no fetch — matches the server 403 without firing it
    if (machine.id) dispatch(getKioskTasks({ machineId: machine.id }));
  }, [machine.id, noTasksForRole, dispatch]);

  if (noTasksForRole) return null;

  // Scoped to THIS machine (BUG-3), not failed, and non-empty — else hidden.
  const scoped = tasks && tasksMachineId === machine.id.toLowerCase() ? tasks : undefined;
  if (!scoped || stTasks.failed) return null;
  const count = openTaskCount(scoped);
  if (count === 0) return null;

  const tone = scoped.some((t) => t.isOverdue) ? "--red" : "--amber";

  const go = () => {
    if (machine.internalNumber) navigate(`/public-v2/machines/${machine.internalNumber}/tasks`);
  };

  return (
    <DashTile
      glyph={GD.task}
      label={translate(tasksRowLabelKey(role))}
      accent={`var(${tone})`}
      count={count}
      onClick={go}
    />
  );
}

export default MyTasksRow;
